import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, Sparkles } from "lucide-react";
import { useSite } from "../context/SiteContext";
import HeroVisual from "../components/site/HeroVisual";

export const Hero = () => {
    const { content } = useSite();
    const h = content.hero || {};
    const title = (h.title || "").split("\n");
    const waNum = (content.social?.whatsapp || "").replace(/\D/g, "");

    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative min-h-[100svh] flex items-center bg-white pt-28 pb-16 lg:pt-32 lg:pb-20 overflow-hidden"
        >
            {/* soft gradient orbs */}
            <div className="absolute top-32 -left-32 w-[500px] h-[500px] rounded-full bg-[#0A1F3D] opacity-[0.05] blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 -right-32 w-[500px] h-[500px] rounded-full bg-[#14545E] opacity-[0.07] blur-3xl pointer-events-none" />

            <div className="relative z-10 w-full max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                <div className="lg:col-span-6 space-y-7">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0A1F3D]/[0.04] text-[#0A1F3D] text-xs tracking-wider border border-[#0A1F3D]/10"
                    >
                        <Sparkles size={14} className="text-[#14545E]" />
                        {h.eyebrow || "KA ACADEMY"}
                    </motion.div>

                    <h1
                        data-testid="hero-title"
                        className="font-serif-display text-[44px] sm:text-6xl lg:text-7xl xl:text-[84px] leading-[1.02] text-[#0A1F3D]"
                    >
                        {title.map((line, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    duration: 0.9,
                                    delay: 0.15 + i * 0.12,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                            >
                                {i === title.length - 1 ? (
                                    <span className="text-[#14545E]">{line}</span>
                                ) : (
                                    line
                                )}
                            </motion.div>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.4 }}
                        className="text-neutral-600 leading-relaxed text-base lg:text-lg max-w-xl"
                    >
                        {h.subtitle}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.55 }}
                        className="flex flex-wrap gap-3 pt-1"
                    >
                        <Link
                            to="/elaqe"
                            data-testid="hero-cta-primary"
                            className="group inline-flex items-center gap-2 bg-[#0A1F3D] hover:bg-[#061632] text-white px-6 py-3.5 rounded-full text-sm transition-colors"
                        >
                            {h.ctaPrimary || "Qeydiyyatdan keç"}
                            <ArrowRight
                                size={16}
                                className="transition-transform group-hover:translate-x-1"
                            />
                        </Link>
                        {waNum && (
                            <a
                                href={`https://wa.me/${waNum}`}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="hero-cta-secondary"
                                className="inline-flex items-center gap-2 border border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white px-6 py-3.5 rounded-full text-sm transition-colors"
                            >
                                <MessageCircle size={16} />
                                {h.ctaSecondary || "WhatsApp ilə yaz"}
                            </a>
                        )}
                    </motion.div>

                    {/* Differentiator badge */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 0.8 }}
                        className="pt-8 border-t border-neutral-200 max-w-md"
                    >
                        <div className="flex items-center gap-3">
                            <div className="h-12 w-12 rounded-2xl bg-[#14545E] flex items-center justify-center text-white font-serif-display">
                                01
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                                    Fərqli yanaşma
                                </div>
                                <div className="font-serif-display text-lg text-[#0A1F3D]">
                                    Əvvəl psixoloq, sonra tədris.
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="lg:col-span-6"
                >
                    <HeroVisual />
                </motion.div>
            </div>
        </section>
    );
};

export default Hero;
